import _ from 'lodash'
import ConfigurationData, { ConfigurationDataFull } from '../../types/Configuration'
import {
  ConstelaConfiguration,
  ConstelaConfigurationModule,
  ConstelaExpertConfiguration,
  ConstelaInspirationConfiguration
} from '../../types/ConstelaConfiguration'
import ModuleData, { isServiceData, isVariableModule } from '../../types/ModuleData'
import { PortfolioItem } from '../../types/Portfolio'
import {
  ConstraintData,
  FilteredPropertyData,
  FilteredPropertyDataValue,
  PropertyData,
  PropertyDataValue
} from '../../types/PropertyData'
import { getSystemHeight } from '../store/hooks/configurationHooks'
import { isDefined } from '../utils/typeGuards'
import { getCurrentModuleVariation } from './ModuleData'

export const convertInspirationToExpertConfiguration = (
  config: ConstelaInspirationConfiguration | ConstelaExpertConfiguration,
  data: ConfigurationDataFull
): ConstelaExpertConfiguration => {
  if (config.type === 'expert') {
    return config
  }
  const serviceDatas = config.services.map((service) => {
    const moduleData = data.modules.find((x) => x.id === service.id)
    if (!isServiceData(moduleData)) {
      return undefined
    }
    return getCurrentModuleVariation(moduleData, config.properties, service.properties)
  })
  const propertiesFromServices: Record<string, string> = Object.assign(
    {},
    ...config.services.map((service, i) => {
      const currentModuleData = serviceDatas[i]
      return currentModuleData?.equivalentProperty ?? {}
    })
  )
  const nextConfiguration = modifyGlobalProperties(
    {
      ...config,
      services: config.services.filter(
        (service, i) => !!serviceDatas[i] && !serviceDatas[i]?.equivalentProperty && !serviceDatas[i]?.expertHidden
      ),
      type: 'expert'
    },
    propertiesFromServices,
    data
  )
  return nextConfiguration
}

export const ruleMatchesProperties = <
  T extends {
    matchAny: Record<string, string | string[]>[]
  }
>(
  rule: T,
  configurationProperties: Record<string, string>
): boolean => {
  // Is there any matching matchAny group?
  return rule.matchAny.some((pattern) =>
    // Does this pattern match?
    Object.entries(pattern).every(([key, value]) => {
      // Does this pattern match on this key/value combination
      if (!(key in configurationProperties)) {
        return false
      }
      if (typeof value === 'string') {
        return value === configurationProperties[key]
      } else {
        return value.includes(configurationProperties[key])
      }
    })
  )
}

const disabledPriority = (disabled: FilteredPropertyDataValue['disabled'] | 'hidden' | 'disabled'): number => {
  if (typeof disabled !== 'string') {
    return disabled ? disabledPriority(disabled.type) : 0
  }
  return disabled === 'hidden' ? 2 : 1
}

/**
 * WARNING: Will modify the properties
 */
const filterValues = (
  properties: FilteredPropertyData[],
  list: Record<string, string[]> | undefined,
  type: 'whitelist' | 'blacklist',
  constraint: ConstraintData
): void => {
  for (const [key, values] of Object.entries(list ?? {})) {
    const property = properties.find((x) => x.id === key)
    if (property) {
      property.values = property.values.map((value) => {
        if (
          values.includes(value.id) === (type === 'whitelist') ||
          disabledPriority(value.disabled) >= disabledPriority(constraint.type ?? 'disabled')
        ) {
          return value
        }
        return {
          ...value,
          disabled: {
            reason: constraint.id,
            type: constraint.type ?? 'disabled'
          }
        }
      })
    }
  }
}

export const filterProperties = (
  { properties, constraints }: Pick<ConfigurationData, 'properties' | 'constraints'>,
  configurationProperties: Record<string, string>,
  additionalProperties: { module: 'global' } | ({ module: string } & { [key: string]: string })
): FilteredPropertyData[] => {
  const result: FilteredPropertyData[] = properties.map((x) => ({
    ...x,
    values: x.values.map((val) => ({ ...val, disabled: false }))
  }))
  const _configurationProperties = { ...additionalProperties, ...configurationProperties }
  for (const constraint of constraints) {
    if (ruleMatchesProperties(constraint, _configurationProperties)) {
      filterValues(result, constraint.blacklistValues, 'blacklist', constraint)
      filterValues(result, constraint.whitelistValues, 'whitelist', constraint)
    }
  }
  return result
}

const getDefaultValue = (property?: PropertyData | FilteredPropertyData): string => {
  if (!property) {
    return ''
  }
  const values = property.values.filter((x: PropertyDataValue | FilteredPropertyDataValue) =>
    'disabled' in x ? !x.disabled : true
  )
  const defaultValue =
    (typeof property.default === 'string' ? [property.default] : property.default ?? []).find((id) =>
      values.find((x) => x.id === id)
    ) ??
    values[0]?.id ??
    ''
  return defaultValue
}

export const makeValidConfig = (
  { properties, constraints, modules }: Pick<ConfigurationData, 'properties' | 'constraints' | 'modules'>,
  configurationProperties: Record<string, string>,
  additionalProperties: { module: 'global' } | ({ module: string } & { [key: string]: string })
): Record<string, string> => {
  const module =
    additionalProperties.module === 'global' ? undefined : modules.find((x) => x.id === additionalProperties.module)
  const neccessaryProperties =
    module && 'propertyMenu' in module
      ? (module.propertyMenu ?? []).map((x) => properties.find((p) => p.id === x.property)).filter(isDefined)
      : additionalProperties.module === 'global'
      ? properties.filter((x) => x.isConfigProperty)
      : []
  if (neccessaryProperties.length === 0) {
    return {}
  }

  const result: Record<string, string> = {}
  for (const property of neccessaryProperties) {
    result[property.id] = configurationProperties[property.id] ?? getDefaultValue(property)
  }

  const makeValid = (configurationProperties: Record<string, string>): Record<string, string> => {
    const filteredProperties = filterProperties({ properties, constraints }, result, additionalProperties)
    const invalidProperty = Object.entries(configurationProperties).find(
      ([key, value]) => !filteredProperties.find((x) => x.id === key)?.values.find((x) => !x.disabled && x.id === value)
    )
    if (!invalidProperty) {
      return configurationProperties
    } else {
      if (invalidProperty[0] === 'switchingType' && invalidProperty[1] === '') {
        throw new Error()
      }
    }
    return makeValid({
      ...configurationProperties,
      [invalidProperty[0]]: getDefaultValue(filteredProperties.find((x) => x.id === invalidProperty[0]))
    })
  }

  return makeValid(result)
}

const getTriluxId = (
  module: ConstelaConfigurationModule,
  globalProperties: Record<string, string>,
  data: ConfigurationDataFull
): string | undefined => {
  const moduleData = getCurrentModuleVariation(
    data.modules.find((x) => x.id === module.id),
    globalProperties,
    module.properties
  )
  const result = moduleData?.triluxId
  if (typeof result === 'undefined') {
    return undefined
  }
  const properties: Record<string, string> = {
    ...globalProperties,
    ...(module.properties ?? {}),
    module: module.id
  }
  for (const { name, property, values } of moduleData?.triluxIdParts ?? []) {
    if (!(property in properties)) {
      console.warn(`triluxIdPart ${JSON.stringify(name)} references unknown property ${JSON.stringify(property)}`)
    }
    const value = properties[property]
    if (!(value in values)) {
      console.warn(
        `triluxIdPart ${JSON.stringify(name)} references unknown value ${JSON.stringify(
          value
        )} for property ${JSON.stringify(property)}`
      )
    }
    properties[name] = values[value]
  }

  return result.replace(/{[\w-]+}/g, (token) => {
    const varname = token.substring(1, token.length - 1)
    return properties[varname]
  })
}

export const getBOM = (
  configuration: ConstelaConfiguration,
  data: ConfigurationDataFull
): {
  module: ConstelaConfigurationModule
  triluxId: string | undefined
  portfolioItem: PortfolioItem | undefined
}[] => {
  const modules = [
    configuration.lightingHead,
    ...configuration.modules,
    configuration.supportingColumn,
    ...data.modules
      .map((x): ConstelaConfigurationModule | undefined => {
        if (x.type === 'ImplicitModule' && ruleMatchesProperties(x.condition, configuration.properties)) {
          return { id: x.id }
        }
      })
      .filter(isDefined)
  ]

  return modules.map((module) => {
    const triluxId = getTriluxId(module, configuration.properties, data)
    return {
      module,
      triluxId,
      portfolioItem: triluxId ? data.portfolio.find((x) => x.id === triluxId) : undefined
    }
  })
}

export const getNumberedModuleTitles = (
  modules?: ConstelaConfigurationModule[],
  moduleData?: ModuleData[],
  skipNumbers?: boolean
): string[] => {
  if (!modules) {
    return []
  }
  const result = modules.map((x) => ({ id: x.id, title: '' }))
  for (const module of result) {
    if (module.title) {
      continue
    }
    const title = moduleData?.find((x) => x.id === module.id)?.title ?? module.id
    result
      .filter((x) => x.id === module.id)
      .forEach((m2, i, modules2) => {
        if (skipNumbers || modules2.length < 2) {
          m2.title = title
        } else {
          m2.title = `${title} ${i + 1}`
        }
      })
  }
  return result.map((x) => x.title)
}

const limitHeight = <T extends ConstelaConfiguration>(configuration: T, data: ConfigurationDataFull): T => {
  const availableModules = data.modules
  let variableModules = configuration.modules.filter((x) =>
    isVariableModule(availableModules.find(({ id }) => id === x.id))
  )
  const fixedModules = configuration.modules.filter(
    (x) => !isVariableModule(availableModules.find(({ id }) => id === x.id))
  )
  const maxHeight = data.system.maxHeight

  while (
    variableModules.length &&
    getSystemHeight(availableModules, {
      ...configuration,
      modules: variableModules
    }) > maxHeight
  ) {
    variableModules = variableModules.slice(0, variableModules.length - 1)
  }

  return {
    ...configuration,
    modules: fixedModules.concat(variableModules)
  }
}

const fixModule = <T extends ConstelaConfiguration>(
  module: ConstelaConfigurationModule,
  configuration: T,
  data: ConfigurationDataFull
): ConstelaConfigurationModule => {
  const properties = makeValidConfig(data, module.properties ?? {}, { ...configuration.properties, module: module.id })
  if (_.isEqual(properties, module.properties)) {
    return module
  }
  return Object.keys(properties).length
    ? {
        id: module.id,
        properties
      }
    : { id: module.id }
}

export const modifyGlobalProperties = <T extends ConstelaConfiguration>(
  configuration: T,
  nextProperties: Record<string, string>,
  data: ConfigurationDataFull
): T => {
  let nextConfiguration: T = { ...configuration }
  nextConfiguration.properties = makeValidConfig(
    data,
    {
      ...nextConfiguration.properties,
      ...nextProperties
    },
    {
      module: 'global'
    }
  )
  nextConfiguration.modules = nextConfiguration.modules.map((moduleData) =>
    fixModule(moduleData, nextConfiguration, data)
  )
  nextConfiguration = limitHeight(nextConfiguration, data)
  return nextConfiguration
}

export const modifyModuleProperties = <T extends ConstelaConfiguration>(
  configuration: T,
  moduleIndex: number,
  nextProperties: Record<string, string>,
  data: ConfigurationDataFull
): T | undefined => {
  const moduleData = configuration.modules[moduleIndex]
  if (!moduleData) {
    return
  }
  const nextModule = fixModule(
    {
      ...moduleData,
      properties: {
        ...moduleData.properties,
        ...nextProperties
      }
    },
    configuration,
    data
  )
  const nextConfiguration = {
    ...configuration,
    modules: configuration.modules.map((x, i) => (i === moduleIndex ? nextModule : x))
  }
  if (getSystemHeight(data.modules, nextConfiguration) <= data.system.maxHeight) {
    return nextConfiguration
  }
}

export const setConfigurationModules = <T extends ConstelaConfiguration>(
  configuration: T,
  modules: ConstelaConfigurationModule[],
  data: ConfigurationDataFull
): T => {
  const nextModules = modules.map((module) => fixModule(module, configuration, data))
  const nextConfiguration = limitHeight(
    {
      ...configuration,
      modules: nextModules
    },
    data
  )
  return nextConfiguration
}
